import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, ButtonBase, CircularProgress, IconButton, Typography } from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AlertModal from "../../../Components/Modals/AlertModal";
import SearchField from "../../../Components/SearchField";
import InputField from "../../../Components/InputField";
import SingleSelectDropdown from "../../../Components/CustomDropdowns/SingleSelectDropdown";
import { useSnackbar } from "../../../Components/SnackBar";
import { aiErrorKey, useSavedAnswers } from "../hooks/useCommunityAi";
import { ASKER_SOURCES } from "../mockData";
import AnswerCard from "./AnswerCard";
import { CARD_BORDER, GREEN, GREEN_ACCENT, INPUT_BORDER, SOFT_BG, TEXT_MAIN, TEXT_SUB, g } from "../styles";

// Search appears once there are more answers than fit at a glance.
const SEARCH_FROM = 4;
const MODES = ["text", "image"];
// Same limits as the API (COMY1-373 §6) — checked here first so the manager hears it at once.
export const MAX_TEXT = 2000;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];

export const toolStyles = {
  form: { display: "flex", flexDirection: "column", gap: "12px" },
  // Secondary choice inside a tool — pills, so it reads below the column's tabs.
  modes: { display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "12px" },
  error: { fontSize: "12px", color: "rgba(200, 55, 55, 1)", marginTop: "-4px" },
  submitRow: { display: "flex", alignItems: "center", gap: "10px" },
  loading: { fontSize: "13px", color: TEXT_SUB },
  savedHeader: { fontSize: "15px", fontWeight: 500, color: TEXT_MAIN, borderTop: CARD_BORDER, paddingTop: "16px", marginTop: "16px", marginBottom: "10px" },
  savedEmpty: { fontSize: "13px", color: TEXT_SUB, lineHeight: 1.5 },
  savedList: { display: "flex", flexDirection: "column", gap: "8px" },
  listState: { display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", color: TEXT_SUB },
};

/** The saved list's own loading / error line — the rest of the tool keeps working. */
export const SavedListState = ({ status, onRetry, t }) =>
  status === "loading" ? (
    <Box sx={toolStyles.listState} role="status">
      <CircularProgress size={16} sx={{ color: GREEN }} />
      <Typography component="span" sx={{ fontSize: "13px", color: TEXT_SUB }}>{t("ai.saved_loading")}</Typography>
    </Box>
  ) : (
    <Box sx={toolStyles.listState} role="alert">
      <Typography component="span" sx={{ fontSize: "13px", color: TEXT_SUB }}>{t("ai.saved_error")}</Typography>
      <Button size="small" onClick={onRetry} sx={{ color: GREEN, minWidth: 0 }}>{t("ai.retry")}</Button>
    </Box>
  );

const s = {
  askerRow: { display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)", gap: "10px", alignItems: "end" },
  fieldLabel: { fontSize: "15px", fontWeight: 500, color: TEXT_MAIN, marginBottom: "8px" },
  // Screenshot mode: one clear target to choose (or paste) the image.
  dropzone: {
    width: "100%",
    minHeight: "104px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    border: `1px dashed ${INPUT_BORDER}`,
    borderRadius: "8px",
    backgroundColor: SOFT_BG,
    padding: "12px",
    "&:hover": { borderColor: GREEN_ACCENT },
    "&.Mui-focusVisible": { outline: `2px solid ${GREEN_ACCENT}` },
  },
  dropTitle: { fontSize: "14px", fontWeight: 500, color: TEXT_MAIN },
  dropSub: { fontSize: "12px", color: TEXT_SUB },
  preview: { position: "relative", border: CARD_BORDER, borderRadius: "8px", overflow: "hidden", backgroundColor: SOFT_BG },
  previewImage: { width: "100%", maxHeight: "180px", objectFit: "contain", display: "block" },
  previewRemove: {
    position: "absolute",
    top: "6px",
    insetInlineEnd: "6px",
    width: "26px",
    height: "26px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    "&:hover": { backgroundColor: "#fff" },
  },
  search: { marginBottom: "10px", "& .MuiFormControl-root, & .MuiTextField-root": { width: "100%" } },
};

const imageFromClipboard = (event) =>
  Array.from(event.clipboardData?.files || []).find((file) => file.type.startsWith("image/"));

const matchesSearch = (answer, query) =>
  [answer.asker?.name, answer.input.text, answer.question].some((value) => value && value.toLowerCase().includes(query));

// "Reply to a question" tool, inside the page's AI column.
const ReplyHelper = ({ lang, t }) => {
  const { answers, status, reload, ask, rename, toggleStep, refresh, remove } = useSavedAnswers(lang);
  const snackbar = useSnackbar();
  // One input at a time: the message text, or a screenshot of it.
  const [mode, setMode] = useState("text");
  const [text, setText] = useState("");
  const [image, setImage] = useState(null); // { name, url, file }
  const [askerName, setAskerName] = useState("");
  const [source, setSource] = useState(ASKER_SOURCES[0]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(answers[0]?.id ?? null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const fileInput = useRef(null);

  useEffect(() => () => image && URL.revokeObjectURL(image.url), [image]);

  const attach = useCallback(
    (file) => {
      if (!file) return;
      setMode("image");
      if (!IMAGE_TYPES.includes(file.type)) {
        setError(t("ai.errors.image_type"));
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        setError(t("ai.errors.image_too_large"));
        return;
      }
      setImage({ name: file.name, url: URL.createObjectURL(file), file });
      setError("");
    },
    [t],
  );

  // In screenshot mode, Ctrl+V anywhere on the page attaches the image.
  useEffect(() => {
    if (mode !== "image") return undefined;
    const onPaste = (event) => {
      const file = imageFromClipboard(event);
      if (file) {
        event.preventDefault();
        attach(file);
      }
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [mode, attach]);

  const submit = async () => {
    const isImage = mode === "image";
    if (isImage ? !image : !text.trim()) {
      setError(t(isImage ? "helper.empty_image" : "helper.empty_text"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const answer = await ask({
        text: isImage ? "" : text.trim(),
        imageFile: isImage ? image.file : null,
        uiLang: lang,
        askerName: askerName.trim(),
        source,
      });
      setExpandedId(answer.id);
      setSearch("");
      if (isImage) setImage(null);
      else setText("");
      setAskerName("");
    } catch (err) {
      // Keep what the manager typed, so "try again" is one click.
      setError(t(aiErrorKey(err)));
    } finally {
      setLoading(false);
    }
  };

  const updateAnswer = async (id, patch) => {
    try {
      await rename(id, patch.asker?.name ?? "");
    } catch {
      snackbar(t("ai.errors.save_failed"), "error");
    }
  };

  const setStepDone = async (id, index, done) => {
    try {
      await toggleStep(id, index, done);
    } catch {
      snackbar(t("ai.errors.save_failed"), "error");
    }
  };

  const recheckAnswer = async (id) => {
    try {
      await refresh(id, lang);
      snackbar(t("helper.rechecked"), "success");
    } catch (err) {
      snackbar(t(aiErrorKey(err)), "error");
    }
  };

  const removeAnswer = async (id) => {
    try {
      await remove(id);
    } catch {
      snackbar(t("ai.errors.delete_failed"), "error");
    }
  };

  const query = search.trim().toLowerCase();
  const visibleAnswers = query ? answers.filter((answer) => matchesSearch(answer, query)) : answers;

  return (
    <Box>
      <Box role="group" aria-label={t("helper.mode_label")} sx={toolStyles.modes}>
        {MODES.map((value) => (
          <Button
            key={value}
            variant="outlined"
            size="small"
            onClick={() => {
              setMode(value);
              setError("");
            }}
            aria-pressed={mode === value}
            disabled={loading}
            sx={g.toggleChip(mode === value)}
          >
            {t(`helper.mode_${value}`)}
          </Button>
        ))}
      </Box>

      <Box sx={toolStyles.form}>
        {mode === "text" ? (
          // Pasting a picture into the text box means the manager has a screenshot.
          <Box
            onPaste={(e) => {
              const file = imageFromClipboard(e);
              if (file) {
                e.preventDefault();
                attach(file);
              }
            }}
          >
            <InputField
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                if (error) setError("");
              }}
              placeholder={t("helper.placeholder")}
              multiline
              rows={4}
              maxLength={MAX_TEXT}
              disabled={loading}
            />
          </Box>
        ) : image ? (
          <Box sx={s.preview}>
            <Box component="img" src={image.url} alt={image.name} sx={s.previewImage} />
            <IconButton size="small" onClick={() => setImage(null)} sx={s.previewRemove} aria-label={t("helper.remove_image")} disabled={loading}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ) : (
          <ButtonBase onClick={() => fileInput.current?.click()} sx={s.dropzone} disabled={loading}>
            <AddPhotoAlternateOutlinedIcon sx={{ color: GREEN }} aria-hidden="true" />
            <Typography component="span" sx={s.dropTitle}>{t("helper.image_pick")}</Typography>
            <Typography component="span" sx={s.dropSub}>{t("helper.image_paste")}</Typography>
          </ButtonBase>
        )}
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          onChange={(e) => {
            attach(e.target.files?.[0]);
            e.target.value = "";
          }}
        />

        <Box sx={s.askerRow}>
          <InputField
            label={t("helper.asker_label")}
            value={askerName}
            onChange={(e) => setAskerName(e.target.value)}
            placeholder={t("helper.asker_placeholder")}
            maxLength={100}
            disabled={loading}
          />
          <Box>
            <Typography sx={s.fieldLabel}>{t("helper.source_label")}</Typography>
            <SingleSelectDropdown
              value={source}
              onChange={(value) => setSource(value)}
              options={ASKER_SOURCES.map((value) => ({ value, label: t(`helper.sources.${value}`) }))}
              disabled={loading}
            />
          </Box>
        </Box>

        {error && <Typography sx={toolStyles.error} role="alert">{error}</Typography>}

        <Box sx={toolStyles.submitRow}>
          <Button variant="contained" onClick={submit} disabled={loading} sx={g.primaryBtn}>
            {t("helper.submit")}
          </Button>
          {loading && (
            <>
              <CircularProgress size={18} sx={{ color: GREEN }} />
              <Typography component="span" sx={toolStyles.loading}>{t("helper.loading")}</Typography>
            </>
          )}
        </Box>
      </Box>

      <Typography component="h3" sx={toolStyles.savedHeader}>
        {t("helper.saved_title", { count: answers.length })}
      </Typography>

      {answers.length >= SEARCH_FROM && (
        <Box sx={s.search}>
          <SearchField searchValue={search} setSearchValue={setSearch} placeholderText={t("helper.search_placeholder")} />
        </Box>
      )}

      {status !== "ready" ? (
        <SavedListState status={status} onRetry={reload} t={t} />
      ) : answers.length === 0 ? (
        <Typography sx={toolStyles.savedEmpty}>{t("helper.saved_empty")}</Typography>
      ) : visibleAnswers.length === 0 ? (
        <Typography sx={toolStyles.savedEmpty}>{t("helper.search_empty")}</Typography>
      ) : (
        <Box sx={toolStyles.savedList}>
          {visibleAnswers.map((answer) => (
            <AnswerCard
              key={answer.id}
              answer={answer}
              lang={lang}
              t={t}
              expanded={expandedId === answer.id}
              onToggle={() => setExpandedId((id) => (id === answer.id ? null : answer.id))}
              onUpdate={(patch) => updateAnswer(answer.id, patch)}
              onToggleStep={(index, done) => setStepDone(answer.id, index, done)}
              onRecheck={() => recheckAnswer(answer.id)}
              onDelete={() => setPendingDelete(answer.id)}
            />
          ))}
        </Box>
      )}

      <AlertModal
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onActionClick={() => {
          const id = pendingDelete;
          setPendingDelete(null);
          removeAnswer(id);
        }}
        title={t("helper.delete_title")}
        description={t("helper.delete_body")}
        actionBtnText={t("helper.delete")}
      />
    </Box>
  );
};

export default ReplyHelper;
